import { type EventCallback, EventEmitter } from '../utils/event-emitter';
import { subscribe, unsubscribe } from './api';
import { SSEEvent, SSEEventMap, SSEMarketBody, SubEvents } from './type';
import type { EventSourceClient, EventSourceMessage, FetchLikeInit, ReadyState } from 'eventsource-client';
import { createEventSource } from 'eventsource-client';
import ky, { Options } from 'ky';

// Use function overloads instead of conditional types
export type SSEApi = {
  readyState: ReadyState | undefined;
  lastEventId: string | undefined;
  connect: () => Promise<string>;
  disconnect: () => void;
  isConnected: () => boolean;
  eventNames: () => SSEEvent[];
  hasListeners: (eventName: SSEEvent) => boolean;
  addEventListener<T extends SubEvents>(
    eventName: T,
    callback: EventCallback<[data: SSEEventMap[T]]>,
    json: SSEMarketBody
  ): Promise<void>;
  addEventListener<T extends Exclude<SSEEvent, SubEvents>>(
    eventName: T,
    callback: EventCallback<[data: SSEEventMap[T]]>,
    json?: SSEMarketBody
  ): Promise<void>;
  removeEventListener<T extends SubEvents>(
    eventName: T,
    callback: EventCallback<[data: SSEEventMap[T]]>,
    json: SSEMarketBody
  ): Promise<void>;
  removeEventListener<T extends Exclude<SSEEvent, SubEvents>>(
    eventName: T,
    callback: EventCallback<[data: SSEEventMap[T]]>,
    json?: SSEMarketBody
  ): Promise<void>;
};

export const isSubEvent = (eventName: SSEEvent): eventName is SubEvents => {
  return ['market:new_trades', 'market:price_change', 'market:info_update'].includes(eventName);
};

export const createSSEApi = (initOptions: Options): SSEApi => {
  const mergedOptions: Options = { ...initOptions, prefixUrl: `${initOptions.prefixUrl}/sse` };
  const sseApi = ky.extend(mergedOptions);
  const emitter = new EventEmitter<{ [K in SSEEvent]: [SSEEventMap[K]] }>();
  const callbackMap = new WeakMap<EventCallback<any>, Map<SSEEvent, EventCallback<any>[]>>();
  const topicRefs = new Map<string, number>();
  const appliedTopics = new Set<string>();
  let sse: EventSourceClient | undefined;
  let connectionId: string | undefined;
  let appliedConnectionId: string | undefined;
  let connectPromise: Promise<string> | null = null;
  let reconcileQueue: Promise<void> = Promise.resolve();
  let reconcileRetryTimer: NodeJS.Timeout | undefined;
  let reconcileRetryAttempts = 0;
  // Reconnection state
  let isManualDisconnect = false;
  let reconnectAttempts = 0;
  let reconnectTimer: NodeJS.Timeout | undefined;
  const maxReconnectDelay = 30000;

  const isConnected = () => !!connectionId;
  const resetConnection = () => {
    connectionId = undefined;
    connectPromise = null;
  };
  const resetAppliedTopics = () => {
    appliedConnectionId = undefined;
    appliedTopics.clear();
  };
  const clearReconcileRetry = () => {
    clearTimeout(reconcileRetryTimer);
    reconcileRetryTimer = undefined;
    reconcileRetryAttempts = 0;
  };
  const disconnect = () => {
    isManualDisconnect = true;
    clearTimeout(reconnectTimer);
    sse?.close();
    sse = undefined;
    resetConnection();
    resetAppliedTopics();
    topicRefs.clear();
    clearReconcileRetry();
  };

  const updateTopicRefs = (topics: string[], delta: 1 | -1) => {
    for (const topic of new Set(topics)) {
      const nextRefCount = (topicRefs.get(topic) ?? 0) + delta;
      if (nextRefCount > 0) {
        topicRefs.set(topic, nextRefCount);
      } else {
        topicRefs.delete(topic);
      }
    }
    if (topicRefs.size === 0) clearReconcileRetry();
  };

  const reconcileTopics = async () => {
    if (topicRefs.size > 0 && !connectionId) {
      await connect();
    }

    const activeConnectionId = connectionId;
    if (!activeConnectionId) {
      resetAppliedTopics();
      return;
    }

    if (appliedConnectionId !== activeConnectionId) {
      resetAppliedTopics();
      appliedConnectionId = activeConnectionId;
    }

    let desiredTopics = new Set(topicRefs.keys());
    const topicsToUnsubscribe = [...appliedTopics].filter(topic => !desiredTopics.has(topic));
    if (topicsToUnsubscribe.length > 0) {
      await unsubscribe(sseApi, { connection_id: activeConnectionId, topics: topicsToUnsubscribe });
      if (connectionId !== activeConnectionId || appliedConnectionId !== activeConnectionId) return;
      for (const topic of topicsToUnsubscribe) appliedTopics.delete(topic);
    }

    desiredTopics = new Set(topicRefs.keys());
    const topicsToSubscribe = [...desiredTopics].filter(topic => !appliedTopics.has(topic));
    if (topicsToSubscribe.length > 0) {
      await subscribe(sseApi, { connection_id: activeConnectionId, topics: topicsToSubscribe });
      if (connectionId !== activeConnectionId || appliedConnectionId !== activeConnectionId) return;
      for (const topic of topicsToSubscribe) appliedTopics.add(topic);
    }
  };

  const enqueueReconcile = () => {
    const operation = reconcileQueue.then(reconcileTopics);
    reconcileQueue = operation.catch(() => undefined);
    return operation;
  };

  const scheduleReconcileRetry = () => {
    const maxRetryAttempts = 5;
    if (
      reconcileRetryTimer ||
      reconcileRetryAttempts >= maxRetryAttempts ||
      isManualDisconnect ||
      !connectionId ||
      topicRefs.size === 0
    )
      return;
    const delay = 1000 * Math.pow(2, reconcileRetryAttempts);
    reconcileRetryAttempts++;
    reconcileRetryTimer = setTimeout(() => {
      reconcileRetryTimer = undefined;
      enqueueReconcile()
        .then(clearReconcileRetry)
        .catch(error => {
          console.error('[SSE] Reconcile retry failed:', error);
          scheduleReconcileRetry();
        });
    }, delay);
  };

  const enqueueReconcileWithRetry = () => {
    const operation = enqueueReconcile();
    void operation.then(clearReconcileRetry, scheduleReconcileRetry);
    return operation;
  };

  const scheduleReconnect = () => {
    if (isManualDisconnect) return;
    const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), maxReconnectDelay);
    console.log(`[SSE] Reconnecting in ${delay}ms... (Attempt ${reconnectAttempts + 1})`);
    reconnectTimer = setTimeout(() => {
      reconnectAttempts++;
      connect()
        .then(() => {
          enqueueReconcileWithRetry().catch(error => console.error('[SSE] Resubscribe failed:', error));
        })
        .catch(err => {
          console.error('[SSE] Reconnect failed:', err);
          scheduleReconnect();
        });
    }, delay);
  };

  const connect = async (): Promise<string> => {
    if (!connectPromise && !sse) {
      isManualDisconnect = false;
    }
    if (connectionId) return connectionId;
    if (connectPromise) return connectPromise;
    if (!sse) {
      sse = createEventSource({
        url: './connect',
        method: 'POST',
        onMessage(message: EventSourceMessage) {
          const { event, data } = message as EventSourceMessage & { event: SSEEvent };
          const result = JSON.parse(data) as SSEEventMap[SSEEvent];
          if (event === 'base:connected') {
            connectionId = (result as SSEEventMap['base:connected']).connection_id;
          }
          emitter.emit(event, result);
        },
        onDisconnect() {
          if (connectionId) {
            emitter.emit('base:disconnected', { connection_id: connectionId });
          }
          sse?.close();
          sse = undefined;
          resetConnection();
          resetAppliedTopics();
          clearReconcileRetry();
          if (!isManualDisconnect) {
            scheduleReconnect();
          }
        },
        fetch: async (url: string | URL, init?: FetchLikeInit) => {
          const options = { ...mergedOptions, ...init };
          return await sseApi(url, options);
        },
      });
    }

    connectPromise = new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        sse?.close();
        sse = undefined;
        resetConnection();
        resetAppliedTopics();
        reject(new Error('Connection timeout'));
      }, mergedOptions.timeout || 10e3);
      const handleConnected = (data: SSEEventMap['base:connected']) => {
        clearTimeout(timeout);
        connectionId = data.connection_id;
        emitter.off('base:connected', handleConnected);
        connectPromise = null;
        reconnectAttempts = 0;
        resolve(connectionId);
      };
      emitter.once('base:connected', handleConnected);
      sse?.connect();
    });
    return connectPromise;
  };

  return {
    connect,
    disconnect,
    readyState: sse?.readyState,
    lastEventId: sse?.lastEventId,
    isConnected,
    eventNames: () => emitter.eventNames(),
    hasListeners: (eventName: SSEEvent) => emitter.hasListeners(eventName),
    addEventListener: async (
      eventName: SSEEvent,
      callback: EventCallback<SSEEventMap[SSEEvent][]>,
      json?: SSEMarketBody
    ) => {
      const wrappedCallback = (data: SSEEventMap[SSEEvent]) => callback(data);
      emitter.on(eventName, wrappedCallback);
      const callbacksByEvent = callbackMap.get(callback) ?? new Map<SSEEvent, EventCallback<any>[]>();
      const callbacks = callbacksByEvent.get(eventName) ?? [];
      callbacks.push(wrappedCallback);
      callbacksByEvent.set(eventName, callbacks);
      callbackMap.set(callback, callbacksByEvent);
      if (isSubEvent(eventName) && json) {
        updateTopicRefs(json.topics, 1);
        await enqueueReconcileWithRetry();
      }
    },
    removeEventListener: async (
      eventName: SSEEvent,
      callback: EventCallback<SSEEventMap[SSEEvent][]>,
      json?: SSEMarketBody
    ) => {
      const callbacksByEvent = callbackMap.get(callback);
      const callbacks = callbacksByEvent?.get(eventName);
      const wrappedCallback = callbacks?.pop();
      if (wrappedCallback) {
        emitter.off(eventName, wrappedCallback);
        if (callbacks?.length === 0) callbacksByEvent?.delete(eventName);
        if (callbacksByEvent?.size === 0) callbackMap.delete(callback);
      }
      if (wrappedCallback && isSubEvent(eventName) && json) {
        updateTopicRefs(json.topics, -1);
        await enqueueReconcileWithRetry();
      }
    },
  };
};

export default createSSEApi;
